import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { ClassroomAssistantSummary } from '../types/backend';
import {
  isApiError,
  listClassroomAssistants,
  redeemClassroomCode,
} from '../api';
import PublicLayout from '../components/layout/PublicLayout';
import {
  assistantChatPath,
  filterClassroomCodeInput,
  isValidClassroomCode,
  normalizeClassroomCode,
} from '../lib/classroomCode';
import {
  clearClassroomGrant,
  isClassroomGrantActive,
  readClassroomGrant,
  writeClassroomGrant,
} from '../lib/classroomGrant';
import { parseBackendUtcTimestamp } from '../lib/liveIntegration';
import { START_NEW_SUBJECT_CONVERSATION_STATE } from '../lib/subjectNavigation';

type PageView = 'form' | 'list';

export default function ClassroomPage() {
  const [codeInput, setCodeInput] = useState('');
  const [view, setView] = useState<PageView>('form');
  const [assistants, setAssistants] = useState<ClassroomAssistantSummary[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const expiryTimerRef = useRef<number | null>(null);
  const loadGenerationRef = useRef(0);

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current !== null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const armExpiryTimer = useCallback(() => {
    clearExpiryTimer();
    const grant = readClassroomGrant();
    if (!grant) {
      return;
    }
    try {
      const expiresAt = parseBackendUtcTimestamp(grant.expires_at);
      const delay = expiresAt - Date.now();
      if (delay <= 0) {
        clearClassroomGrant();
        setView('form');
        setAssistants([]);
        return;
      }
      expiryTimerRef.current = window.setTimeout(() => {
        clearClassroomGrant();
        setView('form');
        setAssistants([]);
      }, delay);
    } catch {
      clearClassroomGrant();
    }
  }, [clearExpiryTimer]);

  const showList = useCallback((rows: ClassroomAssistantSummary[]) => {
    setAssistants(rows);
    setView('list');
    setListError(null);
    armExpiryTimer();
  }, [armExpiryTimer]);

  const showForm = useCallback((options?: { listError?: string | null }) => {
    setView('form');
    setAssistants([]);
    if (options?.listError !== undefined) {
      setListError(options.listError);
    }
  }, []);

  useEffect(() => {
    const generation = ++loadGenerationRef.current;
    let cancelled = false;

    async function loadAssistants() {
      setLoadingList(true);
      try {
        const rows = await listClassroomAssistants();
        if (cancelled || generation !== loadGenerationRef.current) {
          return;
        }
        showList(rows);
      } catch (error) {
        if (cancelled || generation !== loadGenerationRef.current) {
          return;
        }
        if (isApiError(error) && error.code === 'classroom_token_invalid') {
          clearClassroomGrant();
          showForm({ listError: null });
        } else {
          showForm({
            listError: isApiError(error) ? error.userMessage : 'Kunne ikke hente assistenter.',
          });
        }
      } finally {
        if (!cancelled && generation === loadGenerationRef.current) {
          setLoadingList(false);
        }
      }
    }

    void loadAssistants();
    return () => {
      cancelled = true;
      clearExpiryTimer();
    };
  }, [armExpiryTimer, clearExpiryTimer, showForm, showList]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setListError(null);

    const normalized = normalizeClassroomCode(codeInput);
    if (!isValidClassroomCode(normalized)) {
      setFormError('Lærerkoden skal være 6 tegn.');
      return;
    }

    setSubmitting(true);
    loadGenerationRef.current += 1;
    setLoadingList(false);
    try {
      const { expires_at } = await redeemClassroomCode(normalized);
      writeClassroomGrant({ expires_at });
      try {
        const rows = await listClassroomAssistants();
        showList(rows);
      } catch (listErr) {
        showForm({
          listError: isApiError(listErr) ? listErr.userMessage : 'Kunne ikke hente assistenter.',
        });
        armExpiryTimer();
        if (isApiError(listErr) && listErr.code === 'classroom_token_invalid') {
          clearClassroomGrant();
          clearExpiryTimer();
          setListError(null);
        }
      }
    } catch (error) {
      if (isApiError(error)) {
        setFormError(error.userMessage);
      } else {
        setFormError('Noget gik galt. Prøv igen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const grant = readClassroomGrant();
  const showExpiryHint = grant !== null && isClassroomGrantActive();

  return (
    <PublicLayout showFooterCompanyDetails>
      <div
        className="content-page classroom-page"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '56px 24px 80px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <article
          className="content-card"
          style={{
            width: 'min(640px, 100%)',
            background: 'var(--surface-card)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
            padding: '48px clamp(24px, 5vw, 48px)',
          }}
        >
          <h1
            style={{
              margin: '0 0 24px',
              fontSize: 28,
              fontWeight: 600,
              color: 'var(--text-heading)',
            }}
          >
            Klasseværelset
          </h1>

          {view === 'form' ? (
            <div
              className="classroom-intro"
              style={{
                color: 'var(--text-body)',
                fontSize: 16,
                lineHeight: 1.7,
                marginBottom: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <p style={{ margin: 0 }}>Velkommen til klasseværelset.</p>
              <p style={{ margin: 0 }}>
                Skriv kode du har fået udleveret af din lærer og åben op for flere funktioner. Dette
                inkluderer, at op til 40 elever i klassen kan bruge GymGPT samtidig, og at du kan se AI
                assistenter, som din lærer har valgt at dele.
              </p>
            </div>
          ) : null}

          {loadingList && view === 'form' && !formError && !listError ? (
            <p style={{ color: 'var(--fg-2)', margin: 0 }}>Indlæser…</p>
          ) : view === 'form' ? (
            <form onSubmit={(event) => { void handleSubmit(event); }}>
                <label htmlFor="classroom-code" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                  Lærerkode
                </label>
                <input
                  id="classroom-code"
                  name="classroom-code"
                  type="text"
                  className="classroom-code-input"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="text"
                  maxLength={6}
                  value={codeInput}
                  onChange={(event) => setCodeInput(filterClassroomCodeInput(event.target.value))}
                  disabled={submitting}
                />
                {formError ? (
                  <p role="alert" style={{ color: 'var(--danger-text, #b42318)', margin: '0 0 16px' }}>
                    {formError}
                  </p>
                ) : null}
                {listError ? (
                  <p role="alert" style={{ color: 'var(--danger-text, #b42318)', margin: '0 0 16px' }}>
                    {listError}
                  </p>
                ) : null}
                <button type="submit" disabled={submitting} className="classroom-activate-btn">
                  {submitting ? 'Aktiverer…' : 'Aktiver'}
                </button>
              </form>
          ) : (
            <>
              <h2 style={{ margin: '0 0 16px', fontSize: 20 }}>Assistenter</h2>
              {showExpiryHint && grant ? (
                <p style={{ margin: '0 0 16px', color: 'var(--fg-2)', fontSize: 14 }}>
                  Adgang udløber {new Date(parseBackendUtcTimestamp(grant.expires_at)).toLocaleString('da-DK')}.
                </p>
              ) : null}
              {assistants.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-body)' }}>
                  Din lærer har ikke publiceret assistenter endnu.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {assistants.map((assistant) => {
                    const assistantPath = assistantChatPath(assistant.slug);
                    return (
                    <li key={assistant.slug}>
                      {assistantPath ? (
                      <Link
                        to={assistantPath}
                        state={START_NEW_SUBJECT_CONVERSATION_STATE}
                        style={{
                          display: 'block',
                          padding: '14px 16px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-default)',
                          textDecoration: 'none',
                          color: 'var(--text-heading)',
                        }}
                      >
                        <span style={{ display: 'block', fontWeight: 600 }}>{assistant.name}</span>
                        {assistant.description ? (
                          <span style={{ display: 'block', marginTop: 4, color: 'var(--fg-2)', fontSize: 14 }}>
                            {assistant.description}
                          </span>
                        ) : null}
                      </Link>
                      ) : (
                        <div
                          style={{
                            padding: '14px 16px',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-default)',
                            color: 'var(--text-heading)',
                          }}
                        >
                          <span style={{ display: 'block', fontWeight: 600 }}>{assistant.name}</span>
                          {assistant.description ? (
                            <span style={{ display: 'block', marginTop: 4, color: 'var(--fg-2)', fontSize: 14 }}>
                              {assistant.description}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </article>
      </div>
    </PublicLayout>
  );
}
